import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import StartPlanFlow from "../components/StartPlanFlow";
import Button from "../components/ui/Button";
import Reveal from "../components/motion/Reveal";

const steps = [
  { emoji: "📲", title: "Everyone's in", text: "Family members tap a text link and say they're in for tonight." },
  { emoji: "🍿", title: "Kinoes curates", text: "Each person gets ideas — dinner, movies, pop-ups, events — and Kinoes learns what they like." },
  { emoji: "🗳️", title: "Vote & go", text: "It narrows to a few real options everyone votes on. Ties broken from past picks." },
];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 },
  }),
};

const LandingPage: React.FC = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [startOpen, setStartOpen] = useState(false);

  return (
    <div className="mx-auto max-w-4xl">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-4xl bg-sunset px-6 py-16 text-center shadow-warm ring-1 ring-primary-100 sm:px-10 sm:py-20">
        <div
          aria-hidden
          className="animate-float-slow pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary-300/30 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-8 top-24 h-32 w-32 rounded-full bg-dusk-400/20 blur-3xl"
        />

        <motion.p
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="inline-flex items-center gap-2 rounded-full bg-white/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-700 ring-1 ring-primary-200 backdrop-blur"
        >
          🎬 Family night-out planner
        </motion.p>

        <motion.h1
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mx-auto mt-6 max-w-2xl font-display text-5xl font-semibold leading-[1.05] text-ink sm:text-6xl"
        >
          What should we do <span className="italic text-primary-600">tonight?</span>
        </motion.h1>

        <motion.p
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mx-auto mt-5 max-w-xl text-lg text-ink/70"
        >
          Kinoes talks to each person, finds things you can actually do this evening, and gets the
          whole family to a plan you'll all enjoy.
        </motion.p>

        <motion.div custom={3} variants={fadeUp} initial="hidden" animate="show" className="mt-9">
          {isAuthenticated ? (
            <div className="flex flex-col items-center gap-3">
              <Button size="lg" onClick={() => setStartOpen(true)} className="text-lg">
                Start tonight's plan
              </Button>
              <Link to="/dashboard" className="text-sm font-medium text-ink/55 hover:text-ink">
                Manage families &amp; members →
              </Link>
            </div>
          ) : (
            <Button size="lg" onClick={() => loginWithRedirect()} className="text-lg">
              Get started
            </Button>
          )}
        </motion.div>
      </section>

      {/* How it works */}
      <div className="mt-14 grid gap-4 sm:grid-cols-3">
        {steps.map((s, i) => (
          <Reveal key={s.title} index={i}>
            <div className="h-full rounded-3xl border border-primary-100 bg-white/70 p-6 shadow-warm backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cream-200 text-2xl">
                {s.emoji}
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold text-ink">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink/65">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <StartPlanFlow open={startOpen} onClose={() => setStartOpen(false)} />
    </div>
  );
};

export default LandingPage;
